import { SelectedExperienceType } from '../../../../types';
import { some } from '@utils/some';

export type FlattenedExperience =
  | {
      date: string;
      dateRange: string[];
      key: string;
      type: 'group';
      data: { optionGroupId: number; options: SelectedExperienceType[] };
      experienceIndex: number;
    }
  | {
      date: string;
      dateRange: string[];
      key: string;
      type: 'single';
      data: SelectedExperienceType;
      experienceIndex: number;
    };

const countSlots = (opt: {
  options?: SelectedExperienceType[];
  maximumSelections?: number;
}): number => {
  const length = opt.options?.length ?? 0;
  if (length === 0) return 0;
  return opt.maximumSelections ? Math.min(opt.maximumSelections, length) : length;
};

export const flattenExperiences = (selectedExperiencesByDay: any[]): FlattenedExperience[] => {
  return selectedExperiencesByDay.flatMap(({ date, dateRange, options }, dayIndex) => {
    let experienceIndex = 0;

    for (let i = 0; i < dayIndex; i++) {
      const dayOptions = selectedExperiencesByDay[i].options || [];
      dayOptions.forEach((opt) => {
        experienceIndex += countSlots(opt);
      });
    }

    return (options || []).flatMap((exp): FlattenedExperience[] => {
      const hasMultipleOptions = exp.options && exp.options.length > 1;

      if (hasMultipleOptions) {
        const rowCount = countSlots(exp);
        return Array.from(
          { length: rowCount },
          (_, idx): FlattenedExperience => ({
            date,
            dateRange,
            key: `${date}-group-${exp.optionGroupId}-${idx}`,
            type: 'group' as const,
            data: exp,
            experienceIndex: experienceIndex++,
          })
        );
      } else if (some(exp.options)) {
        return exp.options.map((experience): FlattenedExperience => {
          return {
            date,
            dateRange,
            key: `${date}-single-${experience.id}`,
            type: 'single' as const,
            data: experience,
            experienceIndex: experienceIndex++,
          };
        });
      }
      return [];
    });
  });
};
